import type {
	AiAssistantAdminOverviewDto,
	AssistantSettingsDto,
	AssistantSettingsUpdate,
	ProviderFeature,
	ProviderSlotDto,
	ProviderSlotUpdate,
	ProviderTestResultDto,
} from "@/types/ai-assistant-admin";
import type {
	FailoverStatusDto,
	FailoverUpdate,
} from "@/types/ai-assistant-failover";
import type {
	ProviderMode,
	ProviderModeStatusDto,
} from "@/types/ai-assistant-provider-mode";
import type {
	AssistantStyleDto,
	AssistantStyleUpdate,
} from "@/types/ai-assistant-style";
import { normalizeProviders } from "./ai-assistant.logic";

const BASE = "/api/admin/ai-assistant";

/**
 * Endpoint admin tidak terjangkau (mis. server versi lama tanpa route P3) — UI
 * tampil dengan nilai default. Dikenali dari 404, atau dari respons non-JSON
 * (path /api yang tak dikenal bisa jatuh ke fallback SPA yang membalas HTML 200).
 */
export class AdminEndpointUnavailableError extends Error {
	constructor() {
		super("Endpoint admin AI Assistant belum tersedia");
		this.name = "AdminEndpointUnavailableError";
	}
}

export async function request<T>(
	path: string,
	init: RequestInit,
	action: string,
): Promise<T> {
	const res = await fetch(`${BASE}${path}`, {
		...init,
		headers: { "Content-Type": "application/json", ...init.headers },
	});
	if (res.status === 404) throw new AdminEndpointUnavailableError();
	if (!res.ok) {
		const body = (await res.json().catch(() => ({}))) as {
			error?: string;
			message?: string;
		};
		throw new Error(
			`${action} gagal (${res.status}): ${body.error ?? body.message ?? res.statusText}`,
		);
	}
	if (!res.headers.get("content-type")?.includes("application/json"))
		throw new AdminEndpointUnavailableError();
	return (await res.json()) as T;
}

/** Ambil pengaturan, 5 slot kredensial, statistik hari ini, dan kandidat akun kiosk. */
export async function fetchOverview(
	signal?: AbortSignal,
): Promise<AiAssistantAdminOverviewDto> {
	const data = await request<AiAssistantAdminOverviewDto>(
		"",
		{ signal },
		"Memuat pengaturan AI",
	);
	return { ...data, providers: normalizeProviders(data.providers) };
}

/** Simpan pengaturan umum & batas pemakaian. */
export function saveSettings(
	body: AssistantSettingsUpdate,
): Promise<AssistantSettingsDto> {
	return request<AssistantSettingsDto>(
		"/settings",
		{ method: "PUT", body: JSON.stringify(body) },
		"Menyimpan pengaturan",
	);
}

/** Simpan satu slot kredensial (chat/pointer/voice). */
export function saveProvider(
	feature: ProviderFeature,
	body: ProviderSlotUpdate,
): Promise<ProviderSlotDto> {
	return request<ProviderSlotDto>(
		`/providers/${feature}`,
		{ method: "PUT", body: JSON.stringify(body) },
		"Menyimpan kredensial",
	);
}

/** Uji koneksi slot memakai kredensial yang tersimpan di server. */
export function testProvider(
	feature: ProviderFeature,
): Promise<ProviderTestResultDto> {
	return request<ProviderTestResultDto>(
		`/providers/${feature}/test`,
		{ method: "POST" },
		"Test koneksi",
	);
}

/** Status mode penyedia: mode tersimpan, mode yang siap, model OpenAI belum bertarif. */
export function fetchProviderMode(
	signal?: AbortSignal,
): Promise<ProviderModeStatusDto> {
	return request<ProviderModeStatusDto>(
		"/provider-mode",
		{ signal },
		"Memuat mode penyedia",
	);
}

/** Simpan mode penyedia; server menolak (422) bila profil Utama belum terisi. */
export function saveProviderMode(body: {
	mode: ProviderMode;
}): Promise<ProviderModeStatusDto> {
	return request<ProviderModeStatusDto>(
		"/provider-mode",
		{ method: "PUT", body: JSON.stringify(body) },
		"Menyimpan mode penyedia",
	);
}

/** Status pindah otomatis ke Cadangan: sakelar, kesiapan Cadangan, peringatan kuota/kunci. */
export function fetchFailover(
	signal?: AbortSignal,
): Promise<FailoverStatusDto> {
	return request<FailoverStatusDto>(
		"/failover",
		{ signal },
		"Memuat pindah otomatis",
	);
}

/** Nyalakan/matikan pindah otomatis; server menolak (422) bila profil Cadangan belum siap. */
export function saveFailover(body: FailoverUpdate): Promise<FailoverStatusDto> {
	return request<FailoverStatusDto>(
		"/failover",
		{ method: "PUT", body: JSON.stringify(body) },
		"Menyimpan pindah otomatis",
	);
}

/** Ambil gaya & larangan tambahan Jenna (#59). */
export function fetchStyle(signal?: AbortSignal): Promise<AssistantStyleDto> {
	return request<AssistantStyleDto>("/style", { signal }, "Memuat gaya");
}

/** Simpan gaya & larangan tambahan; server memvalidasi ulang. */
export function saveStyle(
	body: AssistantStyleUpdate,
): Promise<AssistantStyleDto> {
	return request<AssistantStyleDto>(
		"/style",
		{ method: "PUT", body: JSON.stringify(body) },
		"Menyimpan gaya",
	);
}
